import {
	EmailBodyStore,
	EnquiriesSearchStore,
	EnquiriesSelectStore,
	EnquiriesStore,
	ModalFlowStore,
	ModalStore,
} from 'Features/SellerCommunications/services/stores';
import { T } from 'Features/SellerCommunications/utils/translate';
import type { SellerCommunicationStatus } from 'Pages/api/seller-communications/types';

import { EMAIL_VALIDATION_REGEXP, EmailBodySchema } from './constants';
import type { EnquiriesStoreState } from './types';
import { ModalSteps } from './types';

export const filterEnquiries = (enquiries: EnquiriesStoreState['enquiries'], keyword: string) =>
	keyword
		? enquiries.reduce((acc, enquiry) => {
				const { make, model, vrm } = enquiry;
				const match = `${vrm} ${make} ${model}`.toLowerCase().includes(keyword.trim().toLowerCase());
				return { ...acc, [enquiry.saleId]: match };
		  }, {})
		: {};

export const validateBody = (body: string): string | null => {
	const result = EmailBodySchema.safeParse(body);
	if (!result.success) {
		return result.error.errors?.[0]?.message;
	}
	const invalidChars = body.match(EMAIL_VALIDATION_REGEXP);
	if (invalidChars) {
		return `${T.translate('createEmail.validationErrors.notValidChars', {
			chars: [...new Set(invalidChars.join(''))].join(', '),
		})}`;
	}
	return null;
};

export interface GroupFailedResultsParams {
	enquiries: EnquiriesStoreState['enquiries'];
	errors: {
		saleId: string;
		status: SellerCommunicationStatus;
	}[];
	groups: {
		statuses: Set<SellerCommunicationStatus>;
		type: string;
	}[];
	translatePath: string;
}

export const groupFailedResults = ({ enquiries, errors, groups, translatePath }: GroupFailedResultsParams) => {
	const enquiriesMap = enquiries.reduce(
		(acc, enquiry) => ({ ...acc, [enquiry.saleId]: enquiry }),
		{} as Record<string, EnquiriesStoreState['enquiries'][number]>,
	);
	return groups.map(({ statuses, type }) => {
		const list = errors
			.filter(({ status }) => statuses.has(status))
			.map(({ saleId }) => enquiriesMap[saleId])
			.filter(Boolean); // Filter out undefined elements
		return {
			enquiries: list,
			title: T.translate(`${translatePath}.${type}`),
		};
	});
};

export const resetStoresState = () => {
	ModalStore.setState({ isOpen: false });
	ModalFlowStore.setState({ currentStep: ModalSteps.EnquirySelectionLoading });
	EmailBodyStore.setState({
		body: '',
		bodyError: null,
		isBodyEmpty: true,
		saveAsDefaultBody: false,
		templateId: '',
	});
	EnquiriesSelectStore.setState({ interactedSaleIds: {}, selectedSaleIds: [] });
	EnquiriesStore.setState({ enquiries: [], enquiriesMap: {}, hasMoreItems: false });
	EnquiriesSearchStore.setState({ filteredSaleIds: {}, keyword: '', noResults: false, search: '' });
};
